/**
 * Zero-pad a number to a fixed width.
 *
 *   pad(3)         → "03"
 *   pad(12, 3)     → "012"
 */
export function pad(n: number, len = 2): string {
  return String(n).padStart(len, "0");
}

/**
 * Three-digit pad — entries are referenced like "001 / 040" in the UI.
 */
export function pad3(n: number): string {
  return pad(n, 3);
}

/**
 * Deterministic Fisher–Yates shuffle. Returns a new array, leaves input
 * untouched. Used by quiz / flashcard / typing screens to randomize order.
 */
export function shuffle<T>(arr: readonly T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Highlight the target word (and basic morphological variants — up to 4
 * trailing letters) inside an example sentence. Returns an array of strings
 * and tuples `{ match: string }` that the caller renders as styled spans.
 */
export interface HighlightToken {
  text: string;
  isMatch: boolean;
}

export function highlightWord(sentence: string, word: string): HighlightToken[] {
  if (!sentence) return [];
  const rx = new RegExp(`(${escapeRegex(word)}[a-z]{0,4})`, "gi");
  return sentence.split(rx).map((text) => ({ text, isMatch: rx.test(text) }));
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Locale-aware display of "today" in the masthead. Uppercase, e.g. "27 MAY 2026".
 */
export function todayShort(date = new Date()): string {
  return date
    .toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })
    .toUpperCase();
}
